import React, { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useImageContext } from '../contexts/ImageContext';
import { useAudio } from '../contexts/AudioContext';
import defaultSpinningDisk from '../images/defaultSpinningDisk.jpg';

/**
 * RouteGuard component that prevents navigation to pages without required data
 * @param {Object} props
 * @param {React.ReactNode} props.children - The page content to render
 * @param {boolean} props.requiresImage - Whether this page requires an uploaded image
 * @param {boolean} props.requiresAudio - Whether this page requires uploaded audio
 * @param {string} props.redirectTo - Where to redirect if requirements not met (default: '/images')
 */
const RouteGuard = ({ 
  children, 
  requiresImage = false, 
  requiresAudio = false,
  redirectTo = '/images'
}) => {
  const navigate = useNavigate();
  const { sessionId } = useParams();
  const { uploadedImage, imageName } = useImageContext();
  const { audioData } = useAudio();

  // Check if a real image has been uploaded (not the default)
  const hasUploadedImage = uploadedImage && 
    (uploadedImage !== defaultSpinningDisk && 
     !uploadedImage.includes('defaultSpinningDisk') &&
     (uploadedImage.startsWith('data:') || imageName !== 'example.jpg'));

  useEffect(() => {
    // Build redirect path with sessionId if present
    const redirectPath = sessionId ? `/app/session/${sessionId}${redirectTo}` : redirectTo;

    // Check if image is required but not uploaded
    if (requiresImage && !hasUploadedImage) {
      navigate(redirectPath, { replace: true });
      return;
    }

    // Check if audio is required but not uploaded
    if (requiresAudio && !audioData?.file) {
      // Redirect to audio page if no audio, or images if no image either
      const audioRedirectPath = sessionId ? `/app/session/${sessionId}/audio` : '/audio';
      const finalRedirectPath = hasUploadedImage ? audioRedirectPath : redirectPath;
      navigate(finalRedirectPath, { replace: true });
      return;
    }
  }, [requiresImage, requiresAudio, hasUploadedImage, audioData, navigate, sessionId, redirectTo]);

  // If requirements are met, render children
  if (requiresImage && !hasUploadedImage) {
    return null; // Don't render while redirecting
  }

  if (requiresAudio && !audioData?.file) {
    return null; // Don't render while redirecting
  }

  return <>{children}</>;
};

export default RouteGuard;

